/**
 * SOURCE BYTES — the one byte drawer every Selection Details pane renders
 * (REQ-UI-184 / REQ-UI-185; ADR-038 §Decision 5).
 *
 * Lifted out of ``query/record-sections.tsx`` on 2026-10-05, when the
 * section stopped being the Query surface's alone: a Platform, Detection,
 * Alert or Track pane shows the same drawer for the same record whether
 * the operator arrived from a recent-data list or from a Query result, so
 * the drawer has to be one component rather than a pattern copied four
 * more times.
 *
 * # What it shows, and why in two renderings
 *
 * **The hex dump** (offset gutter · hex columns · a 16-column ASCII
 * gutter) is the forensic view: byte positions, aligned, pageable. Its
 * gutter maps every non-printable byte to ``.``, which is the hex-dump
 * convention and correct *there* — the gutter exists to hold alignment
 * with the hex beside it.
 *
 * **The text pane** is the readable view, and it is why this module
 * exists. The gutter cannot answer the question an operator actually asks
 * of a stored message — *what did the producer send, in words?* — because
 * for a protobuf payload it reads as runs of dots, indistinguishable from
 * elision. The pane renders the window's **complete** decoding: every
 * byte accounted for, wrapped rather than truncated, control bytes kept
 * as their own identities rather than flattened (see
 * :func:`byte-text.decodeWindowText`).
 *
 * The two panes sit side by side when the Selection Details pane is wide
 * enough for both and wrap to stacked when it is not — plain flex wrap
 * rather than a viewport breakpoint, because this is a *docked* panel
 * whose width has nothing to do with the viewport's.
 *
 * # The Formatted control
 *
 * One control, three outcomes, in preference order:
 *
 *   1. **The decoded SAPIENT field tree** when the descriptor's source is
 *      one the route can be asked about (:data:`DECODABLE_SOURCES` — a
 *      reconstructed wrapper, or a quarantined message's verbatim bytes) —
 *      fetched from the decoded-message route (REQ-QUERY-057 /
 *      REQ-QUERY-058). This is the only rendering of a protobuf payload an
 *      analyst can read, and on a quarantine record it is the view that
 *      answers *why was this refused*.
 *   2. **Pretty-printed JSON or XML** when the window's own text is
 *      structured — the raw-blob and CDS-XML cases.
 *   3. **Disabled, with its reason** when neither applies. Never
 *      offered-then-silently-inert: the same posture the blocked
 *      download takes (REQ-UI-129). The one deliberate exception is a
 *      quarantine payload, whose decode may legitimately fail — see
 *      :data:`DECODABLE_SOURCES` for why offering it is right there.
 *
 * # Disclosure is bounded at every edge
 *
 * Every byte read and every content rendering is an audited disclosure
 * (REQ-QUERY-041 §7), so: the drawer is **closed** by default and reads
 * nothing until opened, and the decoded message is fetched only when the
 * operator asks for the formatted view. Selecting an entity must not
 * emit a disclosure, which is exactly what putting this section on four
 * more panes would otherwise have caused.
 *
 * Cross-references:
 *   - REQ-UI-184 (the section on every entity kind; honest unavailability),
 *     REQ-UI-185 (complete text + the formatted control), REQ-UI-123
 *     (the drawer), REQ-UI-124 (fail-safe integrity), REQ-UI-129
 *     (disabled WITH its reason).
 *   - REQ-QUERY-040..042, 045, 049, 057, 058.
 *   - ADR-038 §Decision 4 / §Decision 5 / D10 / D11.
 */
import { useState } from "react";
import { CaretDown, CaretRight, DownloadSimple } from "@phosphor-icons/react";
import { SectionShell } from "@/entity-details/section-shell";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { decodeWindowText, formatStructuredText } from "@/query/byte-text";
import { queryErrorDetail } from "@/query/use-query-data";
import {
  BYTES_PER_ROW,
  type HexDumpRow,
  formatByteSize,
  formatOffset,
  hexDumpRows,
  integrityBadge,
} from "@/query/hex-dump";
import {
  BYTE_WINDOW_SIZE,
  RAW_BLOB_TYPE,
  type BytesDescriptor,
  blobDownloadUrl,
  useByteWindow,
  useRecordDetail,
  useRecordMessage,
} from "@/query/use-record-detail";

/**
 * Descriptor sources whose bytes the decoded-message route can be asked about.
 *
 * - ``sapient_message`` — reconstructed from a stored ``meridian.v1.data.*``
 *   wrapper, so it IS a SapientMessage and the decode is certain.
 * - ``quarantine_payload`` — the bytes exactly as they arrived
 *   (REQ-QUERY-058). Here the decode is a **question**, not a certainty: a
 *   message quarantined *because* it was malformed will not parse, and that is
 *   the common case rather than an edge.
 *
 * Offering the control for the second is a deliberate departure from
 * REQ-UI-129's disabled-with-its-reason posture, and the reason it is right
 * here is that the field tree is the only rendering that answers *why was this
 * refused*. A refusal costs the operator nothing — the complete byte decoding
 * stays on screen and the route's own message is shown beside it — whereas
 * disabling the control would withhold the one view worth having whenever the
 * payload happened to be decodable.
 */
const DECODABLE_SOURCES: readonly string[] = ["sapient_message", "quarantine_payload"];

export interface SourceBytesSectionProps {
  /**
   * The queryable type whose record carries these bytes, or ``null`` when
   * the entity kind has none at all (a track is a derivation over
   * detections, never a received message).
   */
  readonly typeId: string | null;
  /** The record's natural id within that type. */
  readonly recordId: string;
  /**
   * Why this entity kind carries no originating wire bytes. Rendered when
   * ``typeId`` is ``null``; required there, because a section that simply
   * went blank would read as "this record has none" with no way for the
   * operator to know whether that is a fact or a fault (CLAUDE.md §8.1).
   */
  readonly noBytesNote?: string;
}

/**
 * Render the SOURCE BYTES section for one record.
 *
 * @param typeId - Queryable type, or ``null`` for a kind with no bytes.
 * @param recordId - The record's id within that type.
 * @param noBytesNote - The explanation rendered when ``typeId`` is ``null``.
 */
export function SourceBytesSection({
  typeId,
  recordId,
  noBytesNote,
}: SourceBytesSectionProps): React.ReactElement {
  if (typeId === null) {
    return (
      <SectionShell id="source_bytes" title="Source bytes">
        <p className="text-body-sm text-text-muted" data-testid="record-bytes-unavailable">
          {noBytesNote ?? "This record type has no originating wire bytes."}
        </p>
      </SectionShell>
    );
  }
  return <ResolvedSourceBytes typeId={typeId} recordId={recordId} />;
}

/**
 * The addressable half, split out so the record-detail hook is called
 * unconditionally (rules of hooks) rather than inside the ``typeId``
 * branch above.
 */
function ResolvedSourceBytes({
  typeId,
  recordId,
}: {
  typeId: string;
  recordId: string;
}): React.ReactElement {
  const detail = useRecordDetail(typeId, recordId);

  if (detail.isLoading) {
    return (
      <SectionShell id="source_bytes" title="Source bytes">
        <p role="status" className="text-body-sm text-text-secondary">
          Loading…
        </p>
      </SectionShell>
    );
  }
  if (detail.isError) {
    return (
      <SectionShell id="source_bytes" title="Source bytes">
        <UnreadableRecord error={detail.error} onRetry={() => void detail.refetch()} />
      </SectionShell>
    );
  }

  const bytes = detail.data?.bytes;
  if (bytes === undefined) {
    return (
      <SectionShell id="source_bytes" title="Source bytes">
        <p className="text-body-sm text-text-muted" data-testid="record-bytes-unavailable">
          This record type has no originating wire bytes.
        </p>
      </SectionShell>
    );
  }

  return (
    <SectionShell id="source_bytes" title="Source bytes">
      {bytes.available ? (
        <ByteDrawer typeId={typeId} recordId={recordId} bytes={bytes} />
      ) : (
        <p className="text-body-sm text-text-muted" data-testid="record-bytes-unavailable">
          {byteUnavailableReason(bytes)}
        </p>
      )}
    </SectionShell>
  );
}

/**
 * The record could not be read — said in the server's own words, with the
 * one reading an operator cannot get from an HTTP status.
 *
 * # Why this component exists
 *
 * Until 2026-10-10 this path rendered ``ApiError.message``, which is
 * ``Response.statusText`` — so a detection whose row the store did not hold
 * showed the single word **"Not Found"**. On the surface whose whole job is
 * forensic fidelity that is the worst available answer: it names neither
 * what was not found nor anything the operator might do, and it reads as a
 * broken panel.
 *
 * It also hid a real distinction worth stating. The map's live detections
 * arrive over SSE **straight from NATS** — ``_query_sse.py``'s decoder
 * builds them with ``payload=b""`` / zeroed ``message_hash`` sentinels and
 * notes that "the detail path carries them" — so a detection becomes
 * selectable the instant it is published, while its ``data.detections`` row
 * exists only once the Time-Series writer has flushed. A 404 here is
 * therefore normally **"not stored yet"**: transient, nothing to do with the
 * UI, and self-correcting. A persistent one is a writer that has stopped
 * storing, which is an operational finding the operator should be pointed
 * at rather than left to infer from the word "Not Found".
 *
 * Anything that is *not* a 404 gets no such framing: telling an operator to
 * wait for a writer that is healthy would walk them away from a real fault.
 *
 * # Why a manual retry and not an automatic one
 *
 * :func:`useRecordDetail` treats 4xx as terminal, deliberately — a
 * background poll would put repeated load on the slowest reads this surface
 * makes. So the recovery is the operator's, which means it has to be
 * *reachable*: a real ``<button>``, in the tab order (REQ-UI-059), rather
 * than an instruction to re-select the detection.
 *
 * Cross-references:
 *   - REQ-UI-184 (honest unavailability), REQ-UI-037 (no internal-error
 *     leakage — the BFF's ``detail.message`` is operator-facing text it
 *     authored, not a stack trace), REQ-UI-059 (keyboard reachable).
 *   - CLAUDE.md §8.1 (operator visibility; a dead end is not acceptable).
 */
function UnreadableRecord({
  error,
  onRetry,
}: {
  error: ApiError;
  onRetry: () => void;
}): React.ReactElement {
  // The route's own sentence where it has one ("no sapient_detection record
  // with id 'X'"), which is the same extractor the Query panel uses for its
  // server-side rejections. `statusText` is the last resort, not the default.
  const served = queryErrorDetail(error)?.message ?? null;
  const notFound = error.status === 404;

  return (
    <div className="flex flex-col gap-1.5">
      <p
        role="alert"
        className={cn("text-body-sm", notFound ? "text-text-muted" : "text-destructive")}
        data-testid="record-bytes-detail-error"
      >
        {notFound ? (
          <>
            {served ?? "This record is not in the queryable store."} A detection shown on the map is
            published live, so one selected moments ago may not be stored yet — retry in a few
            seconds. If it stays missing, the store is not receiving what the map is showing.
          </>
        ) : (
          <>Could not load this record&apos;s bytes: {served ?? error.message}</>
        )}
      </p>
      <button
        type="button"
        onClick={onRetry}
        data-testid="record-bytes-retry"
        className="self-start rounded border border-border px-2 py-0.5 text-body-sm text-text-primary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
      >
        Retry
      </button>
    </div>
  );
}

/**
 * Why no bytes can be served — stated, not left blank.
 *
 * Three genuinely different reasons, in order of specificity: the server
 * stated one (a quarantined message whose payload was over the retention
 * ceiling — it HAD bytes, which is not the same as never having had any);
 * the object expired from the bucket while its index row was retained
 * (raw-blob rows deliberately outlive their objects — the bucket carries a
 * 30-day ILM delete, so "the bytes are gone" is a normal state that must
 * read as information rather than as a failure); or the record simply
 * carries none.
 */
function byteUnavailableReason(bytes: BytesDescriptor): string {
  // The server's own reason wins where it has one. A quarantined message whose
  // payload was over the retention ceiling HAD bytes this deployment chose not
  // to keep — telling the operator the generic "this record type has none"
  // would be false, and on a forensic surface that is the worst kind of wrong
  // (CLAUDE.md §8.1).
  if (bytes.unavailable_reason !== null && bytes.unavailable_reason !== undefined) {
    return bytes.unavailable_reason;
  }
  if (bytes.object_available === false) {
    return "The stored object has expired from the object store. The index record is retained, but its bytes are no longer available.";
  }
  return "This record type has no originating wire bytes.";
}

interface ByteDrawerProps {
  typeId: string;
  recordId: string;
  bytes: BytesDescriptor;
}

/** Which rendering the text pane is showing. */
type TextMode = "raw" | "formatted";

/**
 * The expandable drawer: the hex dump, the complete text rendering, the
 * window pager, and (for a raw blob) the whole-object download.
 *
 * Closed by default and fetches nothing until opened: each window read is
 * an audited disclosure, so the drawer must not emit one just because the
 * operator selected an entity.
 */
function ByteDrawer({ typeId, recordId, bytes }: ByteDrawerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [mode, setMode] = useState<TextMode>("raw");
  const window = useByteWindow(typeId, recordId, offset, BYTE_WINDOW_SIZE, {
    enabled: open,
  });

  // Read off the descriptor rather than probed, which is what lets the control
  // be *disabled with its reason* for a source that has no decode at all
  // (a raw blob) instead of enabled-then-refused. See DECODABLE_SOURCES for why
  // a quarantine payload is included even though its decode may fail.
  const hasDecodableMessage =
    bytes.source !== null && bytes.source !== undefined && DECODABLE_SOURCES.includes(bytes.source);
  const decoded = useRecordMessage(typeId, recordId, {
    enabled: open && mode === "formatted" && hasDecodableMessage,
  });

  const total = bytes.total_size_bytes ?? 0;
  // The descriptor's integrity is the record-level verdict; a fetched
  // window carries its own, which wins once we have it.
  const badge = integrityBadge(window.data?.integrity ?? bytes.integrity);
  const rows: readonly HexDumpRow[] =
    window.data === undefined ? [] : hexDumpRows(window.data.data_hex, window.data.offset);
  const hasPrev = offset > 0;
  const hasNext = offset + BYTE_WINDOW_SIZE < total;

  const text = decodeWindowText(window.data?.data_hex ?? "");
  const structured = formatStructuredText(text.text);
  // Enabled when *some* formatted rendering is available. The field tree is
  // preferred over text sniffing: a protobuf payload's own text is framing.
  const canFormat = hasDecodableMessage || structured.format !== "none";
  const formatReason = hasDecodableMessage
    ? null
    : (structured.reason ??
      "This content is not JSON or XML, so there is no structure to lay out.");

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-body-sm">
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls="record-bytes-dump"
          data-testid="record-bytes-toggle"
          className="flex items-center gap-1 rounded border border-border px-2 py-1 text-text-primary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        >
          {open ? (
            <CaretDown size={12} aria-hidden="true" />
          ) : (
            <CaretRight size={12} aria-hidden="true" />
          )}
          {open ? "Hide bytes" : "Show bytes"}
        </button>

        <span className="text-text-secondary" data-testid="record-bytes-size">
          {formatByteSize(total)}
        </span>

        <span
          className={cn("font-medium", badge.className)}
          title={badge.title}
          data-testid="record-bytes-integrity"
        >
          {badge.label}
        </span>

        {typeId === RAW_BLOB_TYPE && <DownloadAction recordId={recordId} bytes={bytes} />}
      </div>

      {open && (
        <div id="record-bytes-dump" data-testid="record-bytes-dump">
          {window.isLoading ? (
            <p role="status" className="text-body-sm text-text-secondary">
              Reading bytes…
            </p>
          ) : window.isError ? (
            // Same posture as the descriptor's failure: the route's own
            // sentence, never the bare `statusText` that rendered as "Not
            // Found". Reachable when the row vanishes between the descriptor
            // fetch and the window read, and when the stored wrapper cannot be
            // unwrapped at all (`bytes_unavailable`).
            <p
              role="alert"
              className="text-body-sm text-destructive"
              data-testid="record-bytes-error"
            >
              {queryErrorDetail(window.error)?.message ?? window.error.message}
            </p>
          ) : (
            <>
              {/* Flex-wrap rather than a viewport breakpoint: this is a docked
                  panel, so the space available has nothing to do with the
                  window size. Two panes when both fit; stacked when not. */}
              <div className="flex flex-wrap gap-2">
                <div className="max-h-64 min-w-[16rem] flex-1 overflow-auto rounded border border-border bg-bg-base p-2">
                  <table className="font-mono text-meta tabular-nums">
                    <caption className="sr-only">
                      Hexadecimal dump of the record&apos;s originating bytes
                    </caption>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.offset} data-testid={`record-bytes-row-${row.offset}`}>
                          <td className="pr-3 text-text-muted">{formatOffset(row.offset)}</td>
                          <td className="whitespace-pre pr-3 text-text-secondary">
                            {row.hex.join(" ").padEnd(BYTES_PER_ROW * 3 - 1, " ")}
                          </td>
                          <td className="whitespace-pre text-text-primary">{row.ascii}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <TextPane
                  mode={mode}
                  onModeChange={setMode}
                  canFormat={canFormat}
                  formatReason={formatReason}
                  rawText={text.text}
                  invalidByteCount={text.invalidByteCount}
                  looksTextual={text.looksTextual}
                  structuredFormat={structured.format}
                  structuredText={structured.text}
                  decodedMessage={decoded.data?.message}
                  decodeLoading={decoded.isLoading}
                  // The BFF's own reason, not the bare status text: a 413 that
                  // reads "Payload Too Large" tells the operator nothing, while
                  // the route's detail names the surface that *can* read the
                  // message. Same extractor the Query panel uses for its
                  // server-side rejections (REQ-UI-129 posture).
                  decodeError={
                    decoded.isError
                      ? (queryErrorDetail(decoded.error)?.message ?? decoded.error.message)
                      : null
                  }
                />
              </div>

              <nav
                aria-label="Byte window"
                className="mt-1 flex items-center gap-2 text-body-sm text-text-secondary"
              >
                <button
                  type="button"
                  onClick={() => setOffset((current) => Math.max(0, current - BYTE_WINDOW_SIZE))}
                  disabled={!hasPrev}
                  data-testid="record-bytes-prev"
                  className="rounded border border-border px-2 py-0.5 text-text-primary hover:bg-bg-elevated disabled:opacity-40"
                >
                  Previous
                </button>
                <span data-testid="record-bytes-window">
                  Bytes {offset.toLocaleString("en-US")}–
                  {Math.min(offset + BYTE_WINDOW_SIZE, total).toLocaleString("en-US")} of{" "}
                  {total.toLocaleString("en-US")}
                </span>
                <button
                  type="button"
                  onClick={() => setOffset((current) => current + BYTE_WINDOW_SIZE)}
                  disabled={!hasNext}
                  data-testid="record-bytes-next"
                  className="rounded border border-border px-2 py-0.5 text-text-primary hover:bg-bg-elevated disabled:opacity-40"
                >
                  Next
                </button>
              </nav>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface TextPaneProps {
  mode: TextMode;
  onModeChange: (mode: TextMode) => void;
  canFormat: boolean;
  formatReason: string | null;
  rawText: string;
  invalidByteCount: number;
  looksTextual: boolean;
  structuredFormat: "json" | "xml" | "none";
  structuredText: string;
  decodedMessage: Record<string, unknown> | undefined;
  decodeLoading: boolean;
  decodeError: string | null;
}

/**
 * The text half of the drawer: the complete decoding of the window, and
 * the Formatted control over it.
 *
 * The rendering is **always** present — the Formatted control changes how
 * it is laid out, never whether the operator can see it. When a formatted
 * rendering is unavailable (the decode was refused, or the content is not
 * structured) the raw decoding stands and the reason is stated beside it,
 * so a failure to format never costs the operator the view of the bytes.
 */
function TextPane({
  mode,
  onModeChange,
  canFormat,
  formatReason,
  rawText,
  invalidByteCount,
  looksTextual,
  structuredFormat,
  structuredText,
  decodedMessage,
  decodeLoading,
  decodeError,
}: TextPaneProps): React.ReactElement {
  const formatted = mode === "formatted";
  const fieldTree = decodedMessage === undefined ? null : JSON.stringify(decodedMessage, null, 2);
  const body = !formatted
    ? rawText
    : fieldTree !== null
      ? fieldTree
      : decodeLoading
        ? "Decoding message…"
        : structuredFormat !== "none"
          ? structuredText
          : rawText;
  const caption = !formatted
    ? null
    : fieldTree !== null
      ? "Decoded message · the fields the producer set, in the SAPIENT standard's own names"
      : structuredFormat === "json"
        ? "Formatted JSON"
        : structuredFormat === "xml"
          ? "Formatted XML"
          : null;

  return (
    <div className="flex min-w-[16rem] flex-1 flex-col gap-1">
      <label
        className="flex items-center gap-1.5 text-body-sm text-text-secondary"
        title={canFormat ? undefined : (formatReason ?? undefined)}
      >
        <input
          type="checkbox"
          checked={formatted}
          disabled={!canFormat}
          data-testid="record-bytes-format-toggle"
          onChange={(event) => onModeChange(event.target.checked ? "formatted" : "raw")}
          className="accent-brand disabled:opacity-40"
        />
        Formatted
        {!canFormat && <span className="sr-only">{formatReason}</span>}
      </label>

      {caption !== null && (
        <p className="text-caption text-text-muted" data-testid="record-bytes-format-caption">
          {caption}
        </p>
      )}

      <pre
        data-testid="record-bytes-text"
        className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-bg-base p-2 font-mono text-meta text-text-primary"
      >
        {body}
      </pre>

      {formatted && decodeError !== null && (
        <p
          role="status"
          className="text-caption text-warning"
          data-testid="record-bytes-format-error"
        >
          {decodeError} The complete byte decoding is shown above.
        </p>
      )}

      {!looksTextual && rawText !== "" && (
        <p className="text-caption text-text-muted" data-testid="record-bytes-text-advisory">
          {invalidByteCount.toLocaleString("en-US")} of these bytes are not valid UTF-8 — this is a
          binary message, not text. Use <b>Formatted</b> to read its decoded fields.
        </p>
      )}
    </div>
  );
}

/**
 * The whole-blob download action (REQ-UI-129 / REQ-QUERY-049).
 *
 * Rendered as a disabled control **with its reason** when the BFF says
 * the blob is not downloadable — over the configured size ceiling, or its
 * object has expired. Offering an action that is going to fail and then
 * explaining afterwards is exactly the silent dead end CLAUDE.md §8.1
 * rules out; the server computes the verdict from the same ceiling the
 * route enforces, so the UI never has to guess.
 */
function DownloadAction({
  recordId,
  bytes,
}: {
  recordId: string;
  bytes: BytesDescriptor;
}): React.ReactElement {
  const blocked = bytes.downloadable === false;
  const reason =
    bytes.download_blocked_reason ??
    "This blob cannot be downloaded. Use the byte window above to read it.";

  if (blocked) {
    return (
      <span
        className="flex items-center gap-1 text-text-muted"
        title={reason}
        data-testid="record-bytes-download-blocked"
      >
        <DownloadSimple size={12} aria-hidden="true" />
        Download unavailable
        <span className="sr-only">{reason}</span>
      </span>
    );
  }

  return (
    <a
      href={blobDownloadUrl(recordId)}
      download
      data-testid="record-bytes-download"
      className="flex items-center gap-1 rounded border border-border px-2 py-0.5 text-text-primary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
    >
      <DownloadSimple size={12} aria-hidden="true" />
      Download blob
    </a>
  );
}
